import type {
  FeatureFlag,
  FeatureFlagCondition,
  FeatureFlagsContextValues,
  FeatureFlagsEnv,
  FeatureFlagsResponse,
} from '../types/feature-flags.types';

/**
 * Evaluates one flag exactly as the backend does
 * (portal/src/Portal/FeatureFlagBundle/ServiceLayer/Service/FeatureFlagService.php).
 *
 * Pure: every input is an argument, so the same call answers identically in a browser, in a
 * Server Component and in a test. Nothing here reads `document`, `window` or a clock.
 */
export function isFlagEnabled(flags: FeatureFlagsResponse, flagName: string, env: FeatureFlagsEnv = {}): boolean {
  const flag = flags[flagName];

  if (!flag?.enabled) {
    return false;
  }

  if (flag.cookie) {
    return env.cookies?.[flag.cookie] === 'true';
  }

  if (!flag.conditions?.length) {
    return true;
  }

  const values = env.context ?? {};

  return flag.conditions.every((condition) => matches(condition, values));
}

/** The flag itself, for a caller that needs its shape rather than its answer. */
export function getFlag(flags: FeatureFlagsResponse, flagName: string): FeatureFlag | undefined {
  return flags[flagName];
}

function matches(condition: FeatureFlagCondition, values: FeatureFlagsContextValues): boolean {
  const contextValue = values[condition.property];

  // The backend skips a condition whose property is missing and then requires every
  // condition to have matched, so a missing property leaves the flag off.
  if (contextValue === undefined) {
    return false;
  }

  switch (condition.operator) {
    case 'eq':
      return condition.value === contextValue;

    case 'ne':
      return condition.value !== contextValue;

    case 'lt':
      if (!isScalar(contextValue) || !isScalar(condition.value)) {
        throw new Error('"Lt" operator requires scalar values');
      }
      return contextValue < condition.value;

    case 'gt':
      if (!isScalar(contextValue) || !isScalar(condition.value)) {
        throw new Error('"Gt" operator requires scalar values');
      }
      return contextValue > condition.value;

    case 'in':
      if (!Array.isArray(condition.value)) {
        throw new Error('"In" operator requires array value');
      }
      return condition.value.includes(contextValue as string);

    case 'nin':
      if (!Array.isArray(condition.value)) {
        throw new Error('"Nin" operator requires array value');
      }
      return !condition.value.includes(contextValue as string);

    default:
      return false;
  }
}

function isScalar(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}
